import React, { useState } from 'react';
import { AuthProvider } from './contexts/AuthContext';
import { PremiumProvider } from './contexts/PremiumContext';
import ProtectedRoute from './components/ProtectedRoute';
import QuizPage from './components/QuizPage';
import LevelTest from './components/LevelTest';
import Explanations from './components/Explanations';
import OldQuestionPage from './components/OldQuestionPage';
import PaymentPage from './components/PaymentPage';
import Footer from './components/Footer';
import { useAuth } from './contexts/AuthContext';
import './App.css';
import 'katex/dist/katex.min.css';

function MainApp() {
  const [currentPage, setCurrentPage] = useState('explanations');
  const { currentUser, userPaymentData, logout, refreshUserData } = useAuth();

  const handleLogout = async () => {
    await logout();
  };

  const handlePaymentSuccess = () => {
    // Payment success is now handled by admin updating user data in Firebase
    // The AuthContext will automatically fetch updated user data
    // Just redirect back to old questions
    setCurrentPage('oldquestions');
  };

  const handleShowPayment = () => {
    setCurrentPage('payment');
  };

  // Refresh user data when navigating to old questions
  const handleOldQuestionsClick = async () => {
    setCurrentPage('oldquestions');
    if (currentUser && refreshUserData) {
      console.log('Refreshing user data for old questions page...');
      await refreshUserData();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Navigation Header */}
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <h1 className="text-xl font-bold text-gray-900">Calculus Helper</h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setCurrentPage('explanations')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  currentPage === 'explanations'
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                Explanations
              </button>
              <button
                onClick={() => setCurrentPage('quiz')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  currentPage === 'quiz'
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                Quiz
              </button>
              <button
                onClick={() => setCurrentPage('leveltest')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  currentPage === 'leveltest'
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                Level Test
              </button>
              <button
                onClick={handleOldQuestionsClick}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  currentPage === 'oldquestions'
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                Old Questions
              </button>

              {/* User Info and Logout */}
              <div className="flex items-center space-x-3 border-l border-gray-200 pl-4">
                <div className="flex items-center space-x-2">
                  <img
                    src={currentUser?.photoURL || 'https://via.placeholder.com/32'}
                    alt="User"
                    className="w-8 h-8 rounded-full"
                  />
                  <span className="text-sm text-gray-700 hidden sm:block">
                    {currentUser?.displayName || currentUser?.email}
                  </span>
                </div>
                <button
                  onClick={handleLogout}
                  className="px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Page Content */}
      {currentPage === 'quiz' ? (
        <QuizPage onShowPayment={handleShowPayment} />
      ) : currentPage === 'leveltest' ? (
        <LevelTest />
      ) : currentPage === 'explanations' ? (
        <Explanations onShowPayment={handleShowPayment} />
      ) : currentPage === 'oldquestions' ? (
        <OldQuestionPage 
          onShowPayment={handleShowPayment}
        />
      ) : currentPage === 'payment' ? (
        <PaymentPage 
          onPaymentSuccess={handlePaymentSuccess}
          onBack={() => setCurrentPage('explanations')}
        />
      ) : (
        <QuizPage onShowPayment={handleShowPayment} />
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <PremiumProvider>
        <ProtectedRoute>
          <MainApp />
        </ProtectedRoute>
      </PremiumProvider>
    </AuthProvider>
  );
}

export default App;
